import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter,
  Field,
  Combobox,
  Option,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import type { EventFilters, OwnerOption, PlannerEventStatus } from '@/types/planner.types';
import { EMPTY_FILTERS } from '@/types/planner.types';
import { formatDate } from '@/utils/dateFormat';
import { useEventFilterPanelStyles } from './EventFilterPanel.styles';

// Adapter for Fluent's DatePicker, which always passes (date | null | undefined).
const formatDateForDatePicker = (date?: Date | null): string => formatDate(date);

// Render the month view as a single compact column whose month/year picker
// expands as an overlay, so the calendar never overflows the filter panel width.
const COLLAPSED_CALENDAR = { showMonthPickerAsOverlay: true } as const;

// Sentinel option values for the in-dropdown "Clear selection" actions. Rendering
// the action as a real Combobox Option (rather than a bare interactive div) keeps
// it inside the listbox option-registration and keyboard-navigation model.
const CLEAR_OWNERS_OPTION = '__clear_owners__';
const CLEAR_STATUSES_OPTION = '__clear_statuses__';
const CLEAR_BUILDING_OPTION = '__clear_building__';

export interface EventFilterPanelProps {
  open: boolean;
  appliedFilters: EventFilters;
  availableOwners: OwnerOption[];
  availableStatuses: PlannerEventStatus[];
  availableBuildings: OwnerOption[];
  onApply: (filters: EventFilters) => void;
  onCancel: () => void;
  ownerIsLoading?: boolean;
  ownerErrorState?: 'NONE' | 'BUILDING_ACCESS' | 'PLANNERS_BY_BUILDING';
  onOwnerRetry?: () => void;
}

export const EventFilterPanel = ({
  open,
  appliedFilters,
  availableOwners,
  availableStatuses,
  availableBuildings,
  onApply,
  onCancel,
  ownerIsLoading = false,
  ownerErrorState = 'NONE',
  onOwnerRetry,
}: EventFilterPanelProps) => {
  const styles = useEventFilterPanelStyles();
  const { t } = useTranslation('planner');

  const [localFilters, setLocalFilters] = useState<EventFilters>(EMPTY_FILTERS);
  const [ownerSearch, setOwnerSearch] = useState<string | undefined>(undefined);
  const [statusSearch, setStatusSearch] = useState<string | undefined>(undefined);
  const [buildingSearch, setBuildingSearch] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setLocalFilters({ ...appliedFilters });
      setOwnerSearch(undefined);
      setStatusSearch(undefined);
      setBuildingSearch(undefined);
    }
  }, [open, appliedFilters]);

  const filteredOwners = useMemo(() => {
    if (!ownerSearch) return availableOwners;
    const q = ownerSearch.trim().toLowerCase();
    if (q.length === 0) return availableOwners;
    return availableOwners.filter((o) => o.displayName.toLowerCase().includes(q));
  }, [availableOwners, ownerSearch]);

  const filteredStatuses = useMemo(() => {
    if (!statusSearch) return availableStatuses;
    const q = statusSearch.trim().toLowerCase();
    if (q.length === 0) return availableStatuses;
    return availableStatuses.filter((s) => s.toLowerCase().includes(q));
  }, [availableStatuses, statusSearch]);

  const filteredBuildings = useMemo(() => {
    if (!buildingSearch) return availableBuildings;
    const q = buildingSearch.trim().toLowerCase();
    if (q.length === 0) return availableBuildings;
    return availableBuildings.filter((b) => b.displayName.toLowerCase().includes(q));
  }, [availableBuildings, buildingSearch]);

  const buildingSelectedText = useMemo(
    () =>
      localFilters.buildings
        .map((id) => availableBuildings.find((b) => b.id === id)?.displayName ?? id)
        .join(', '),
    [localFilters.buildings, availableBuildings]
  );

  const ownerSelectedText = useMemo(
    () =>
      localFilters.owners
        .map((id) => availableOwners.find((o) => o.id === id)?.displayName ?? id)
        .join(', '),
    [localFilters.owners, availableOwners]
  );

  const handleOwnerChange = useCallback((_e: unknown, data: { selectedOptions: string[] }) => {
    setLocalFilters((prev) => ({ ...prev, owners: data.selectedOptions }));
    setOwnerSearch('');
  }, []);

  const handleStatusChange = useCallback((_e: unknown, data: { selectedOptions: string[] }) => {
    setLocalFilters((prev) => ({
      ...prev,
      statuses: data.selectedOptions as PlannerEventStatus[],
    }));
    setStatusSearch(undefined);
  }, []);

  const handleClearOwners = useCallback(() => {
    setLocalFilters((prev) => ({ ...prev, owners: [] }));
  }, []);

  const handleClearStatuses = useCallback(() => {
    setLocalFilters((prev) => ({ ...prev, statuses: [] }));
  }, []);

  const handleClearBuilding = useCallback(() => {
    setLocalFilters((prev) => ({ ...prev, buildings: [] }));
  }, []);

  const handleBuildingChange = useCallback((_e: unknown, data: { selectedOptions: string[] }) => {
    setLocalFilters((prev) => ({ ...prev, buildings: data.selectedOptions }));
    setBuildingSearch('');
  }, []);

  const handleBuildingOptionSelect = useCallback(
    (e: unknown, data: { optionValue?: string; selectedOptions: string[] }) => {
      if (data.optionValue === CLEAR_BUILDING_OPTION) {
        handleClearBuilding();
        setBuildingSearch('');
        return;
      }
      handleBuildingChange(e, data);
    },
    [handleClearBuilding, handleBuildingChange]
  );

  const handleOwnerOptionSelect = useCallback(
    (e: unknown, data: { optionValue?: string; selectedOptions: string[] }) => {
      if (data.optionValue === CLEAR_OWNERS_OPTION) {
        handleClearOwners();
        setOwnerSearch('');
        return;
      }
      handleOwnerChange(e, data);
    },
    [handleClearOwners, handleOwnerChange]
  );

  const handleStatusOptionSelect = useCallback(
    (e: unknown, data: { optionValue?: string; selectedOptions: string[] }) => {
      if (data.optionValue === CLEAR_STATUSES_OPTION) {
        handleClearStatuses();
        setStatusSearch(undefined);
        return;
      }
      handleStatusChange(e, data);
    },
    [handleClearStatuses, handleStatusChange]
  );

  const handleSubmissionStartDateChange = useCallback((date: Date | null | undefined) => {
    setLocalFilters((prev) => {
      const start = date ?? null;
      const end =
        start && prev.submissionEndDate && start > prev.submissionEndDate
          ? null
          : prev.submissionEndDate;
      return { ...prev, submissionStartDate: start, submissionEndDate: end };
    });
  }, []);

  const handleSubmissionEndDateChange = useCallback((date: Date | null | undefined) => {
    setLocalFilters((prev) => ({ ...prev, submissionEndDate: date ?? null }));
  }, []);

  const handleEventStartDateChange = useCallback((date: Date | null | undefined) => {
    setLocalFilters((prev) => {
      const start = date ?? null;
      const end =
        start && prev.eventEndDate && start > prev.eventEndDate ? null : prev.eventEndDate;
      return { ...prev, eventStartDate: start, eventEndDate: end };
    });
  }, []);

  const handleEventEndDateChange = useCallback((date: Date | null | undefined) => {
    setLocalFilters((prev) => ({ ...prev, eventEndDate: date ?? null }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(localFilters);
  }, [localFilters, onApply]);

  return (
    <OverlayDrawer
      position="end"
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) onCancel();
      }}
      data-testid="event-filter-panel"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <BasicButton
              variant="subtle"
              size="medium"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={onCancel}
              data-testid="filter-panel-close"
            />
          }
        >
          {t('filter.panelTitle')}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <div className={styles.fieldGroup}>
          <Field label={t('filter.building')}>
            {availableBuildings.length === 0 ? (
              <span data-testid="filter-building-empty">{t('filter.buildingEmpty')}</span>
            ) : (
              <Combobox
                multiselect
                autoComplete="off"
                placeholder={
                  localFilters.buildings.length > 0
                    ? t('filter.searchBuildings')
                    : t('filter.notSelected')
                }
                selectedOptions={localFilters.buildings}
                onOptionSelect={handleBuildingOptionSelect}
                value={buildingSearch ?? buildingSelectedText}
                onChange={(e) => setBuildingSearch((e.target as HTMLInputElement).value)}
                onOpenChange={(_e, data) => {
                  if (data.open) {
                    setBuildingSearch('');
                  } else {
                    setBuildingSearch(undefined);
                  }
                }}
                listbox={{ className: styles.ownerListbox }}
                data-testid="filter-building-dropdown"
              >
                {localFilters.buildings.length > 0 && (
                  <Option
                    key={CLEAR_BUILDING_OPTION}
                    value={CLEAR_BUILDING_OPTION}
                    text={t('filter.clearSelection')}
                    checkIcon={null}
                    className={styles.inlineClearSelection}
                    data-testid="clear-building-selection-inline"
                  >
                    {t('filter.clearSelection')}
                  </Option>
                )}
                {filteredBuildings.map((building) => (
                  <Option key={building.id} value={building.id} text={building.displayName}>
                    {building.displayName}
                  </Option>
                ))}
                {filteredBuildings.length === 0 && (
                  <Option key="__no_match__" value="__no_match__" disabled text="">
                    {t('filter.buildingEmpty')}
                  </Option>
                )}
              </Combobox>
            )}
            {localFilters.buildings.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearSelectionButton}
                onClick={handleClearBuilding}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClearBuilding();
                }}
                data-testid="clear-building-selection"
              >
                {t('filter.clearSelection')}
              </span>
            )}
          </Field>
        </div>

        <div className={styles.fieldGroup}>
          <Field label={t('filter.owner')}>
            {ownerIsLoading ? (
              <span data-testid="filter-owner-loading">{t('filter.ownerLoading')}</span>
            ) : ownerErrorState !== 'NONE' ? (
              <BasicButton
                variant="secondary"
                size="medium"
                onClick={onOwnerRetry}
                data-testid="filter-owner-retry"
              >
                {t('filter.ownerErrorRetry')}
              </BasicButton>
            ) : availableOwners.length === 0 ? (
              <span data-testid="filter-owner-empty">{t('filter.ownerEmpty')}</span>
            ) : (
              <Combobox
                multiselect
                autoComplete="off"
                placeholder={
                  localFilters.owners.length > 0
                    ? t('filter.searchOwners')
                    : t('filter.notSelected')
                }
                selectedOptions={localFilters.owners}
                onOptionSelect={handleOwnerOptionSelect}
                value={ownerSearch ?? ownerSelectedText}
                onChange={(e) => setOwnerSearch((e.target as HTMLInputElement).value)}
                onOpenChange={(_e, data) => {
                  if (data.open) {
                    setOwnerSearch('');
                  } else {
                    setOwnerSearch(undefined);
                  }
                }}
                listbox={{ className: styles.ownerListbox }}
                data-testid="filter-owner-dropdown"
              >
                {localFilters.owners.length > 0 && (
                  <Option
                    key={CLEAR_OWNERS_OPTION}
                    value={CLEAR_OWNERS_OPTION}
                    text={t('filter.clearSelection')}
                    checkIcon={null}
                    className={styles.inlineClearSelection}
                    data-testid="clear-owner-selection-inline"
                  >
                    {t('filter.clearSelection')}
                  </Option>
                )}
                {filteredOwners.map((owner) => (
                  <Option key={owner.id} value={owner.id} text={owner.displayName}>
                    {owner.displayName}
                  </Option>
                ))}
                {filteredOwners.length === 0 && (
                  <Option key="__no_match__" value="__no_match__" disabled text="">
                    {t('filter.ownerEmpty')}
                  </Option>
                )}
              </Combobox>
            )}
            {localFilters.owners.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearSelectionButton}
                onClick={handleClearOwners}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClearOwners();
                }}
                data-testid="clear-owner-selection"
              >
                {t('filter.clearSelection')}
              </span>
            )}
          </Field>
        </div>

        <div className={styles.fieldGroup}>
          <Field label={t('filter.status')}>
            <Combobox
              multiselect
              autoComplete="off"
              placeholder={t('filter.notSelected')}
              selectedOptions={localFilters.statuses}
              onOptionSelect={handleStatusOptionSelect}
              value={statusSearch ?? localFilters.statuses.join(', ')}
              onChange={(e) => setStatusSearch((e.target as HTMLInputElement).value)}
              onOpenChange={(_e, data) => {
                if (!data.open) setStatusSearch(undefined);
              }}
              data-testid="filter-status-dropdown"
            >
              {localFilters.statuses.length > 0 && (
                <Option
                  key={CLEAR_STATUSES_OPTION}
                  value={CLEAR_STATUSES_OPTION}
                  text={t('filter.clearSelection')}
                  checkIcon={null}
                  className={styles.inlineClearSelection}
                  data-testid="clear-status-selection-inline"
                >
                  {t('filter.clearSelection')}
                </Option>
              )}
              {filteredStatuses.map((status) => (
                <Option key={status} value={status} text={status}>
                  {status}
                </Option>
              ))}
              {filteredStatuses.length === 0 && (
                <Option key="__no_match__" value="__no_match__" disabled text="">
                  {t('filter.noResults')}
                </Option>
              )}
            </Combobox>
            {localFilters.statuses.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearSelectionButton}
                onClick={handleClearStatuses}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClearStatuses();
                }}
                data-testid="clear-status-selection"
              >
                {t('filter.clearSelection')}
              </span>
            )}
          </Field>
        </div>

        <div className={styles.fieldGroup}>
          <Field label={t('filter.submissionStartDate')}>
            <DatePicker
              placeholder={t('filter.notSelected')}
              formatDate={formatDateForDatePicker}
              value={localFilters.submissionStartDate}
              onSelectDate={handleSubmissionStartDateChange}
              calendar={COLLAPSED_CALENDAR}
              data-testid="filter-submission-start-date"
            />
          </Field>
        </div>

        <div className={styles.fieldGroup}>
          <Field label={t('filter.submissionEndDate')}>
            <DatePicker
              placeholder={t('filter.notSelected')}
              formatDate={formatDateForDatePicker}
              value={localFilters.submissionEndDate}
              onSelectDate={handleSubmissionEndDateChange}
              minDate={localFilters.submissionStartDate ?? undefined}
              calendar={COLLAPSED_CALENDAR}
              data-testid="filter-submission-end-date"
            />
          </Field>
        </div>

        <div className={styles.fieldGroup}>
          <Field label={t('filter.eventStartDate')}>
            <DatePicker
              placeholder={t('filter.notSelected')}
              formatDate={formatDateForDatePicker}
              value={localFilters.eventStartDate}
              onSelectDate={handleEventStartDateChange}
              calendar={COLLAPSED_CALENDAR}
              data-testid="filter-event-start-date"
            />
          </Field>
        </div>

        <div className={styles.fieldGroup}>
          <Field label={t('filter.eventEndDate')}>
            <DatePicker
              placeholder={t('filter.notSelected')}
              formatDate={formatDateForDatePicker}
              value={localFilters.eventEndDate}
              onSelectDate={handleEventEndDateChange}
              minDate={localFilters.eventStartDate ?? undefined}
              calendar={COLLAPSED_CALENDAR}
              data-testid="filter-event-end-date"
            />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter className={styles.footer}>
        <BasicButton
          variant="primary"
          size="medium"
          onClick={handleApply}
          data-testid="filter-apply-button"
        >
          {t('filter.apply')}
        </BasicButton>
        <BasicButton
          variant="secondary"
          size="medium"
          onClick={onCancel}
          data-testid="filter-cancel-button"
        >
          {t('filter.cancel')}
        </BasicButton>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

EventFilterPanel.displayName = 'EventFilterPanel';
